<template>
  <SmDialog
    v-model:visibility="visible"
    :title="i18n.t('createLicense')"
    class="dialog"
    size="medium">
    <SmSteps
      v-model:active-step-key="activeStepKey"
      content-height="calc(70vh - 4rem)"
      :steps="steps"
      class="steps-wrapper">
      <template #step-license-model-form>
        <LicenseModelForm
          ref="licenseModelForm"
          v-model:software-license="model"
          label-width="12rem"
          :license-model-id="model.license._id"
          :custom-validators="customValidators" />
      </template>

      <template #step-contract-form>
        <ContractForm
          ref="contractForm"
          v-model:contract="emptyForm"
          @submit="handleFinish"
          :is-fallback="false" />
      </template>
      <template #finish-button>
        <SmButton :loading="finishLoading" @click="handleFinish()">
          {{ i18n.t('create') }}
        </SmButton>
      </template>
    </SmSteps>
  </SmDialog>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import {
    PriceInformation,
    PriceInformationPostIn,
    SoftwareLicense,
    SoftwareLicensePostIn,
  } from '@/client'
  import { CustomValidator } from '@/utils/validator'
  import { sendToast } from '@/components/sm/SmNotification'

  import ContractForm from '@/components/contracts/ContractForm.vue'
  import LicenseModelForm from '@/components/license/LicenseModelForm.vue'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useContractStore } from '@/stores/contractStore'
  import { saveNewSoftwareLicense } from '@/utils/license'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { useRoute } from 'vue-router'

  const props = defineProps<{
    visible: boolean
    customValidators?: CustomValidator[]
    preventSubmit?: boolean
  }>()

  const emit = defineEmits<{
    add: []
    'update:visible': [visible: boolean]
    cancelled: []
  }>()

  const applicationDetailsStore = useApplicationDetailsStore()
  const contractStore = useContractStore()
  const preferenceStore = usePreferenceStore()
  const i18n = useI18n()
  const licenseModelForm = ref<ComponentExposed<
    typeof LicenseModelForm
  > | null>()
  const contractForm = ref<ComponentExposed<typeof ContractForm> | null>()

  const activeStepKey = ref('license-model-form')
  const visible = computed({
    get: () => props.visible,
    set: (val) => emit('update:visible', val),
  })
  const finishLoading = ref(false)

  const licenseModelRequestBody = ref<SoftwareLicensePostIn>()
  const contractRequestBody = ref<PriceInformationPostIn>()

  const route = useRoute()
  const isSSO = computed(() => route.query.sso === 'true')
  const currentSubaccount = computed(() => {
    return route.params.subAccountId as string
  })

  const model = ref<{
    license: SoftwareLicense
  }>({
    license: {} as SoftwareLicense,
  })

  const emptyForm = ref<PriceInformation>({
    _id: '',
    description: '',
    currency: preferenceStore.preferences?.currency_code ?? 'EUR',
  } as PriceInformation)

  const steps = ref([
    {
      key: 'license-model-form',
      nextText: i18n.t('createPriceModel'),
      nextStepValidator: () => {
        return new Promise<boolean>((resolve) => {
          const result = licenseModelForm.value?.validate()

          if (result && licenseModelForm.value) {
            licenseModelRequestBody.value = licenseModelForm.value?.form.fields
          }

          if (isSSO && licenseModelRequestBody.value) {
            licenseModelRequestBody.value.sub_account_id =
              currentSubaccount.value
          }
          resolve(result || false)
        })
      },
    },
    {
      key: 'contract-form',
      backText: i18n.t('editLicense'),
    },
  ])

  async function handleFinish() {
    finishLoading.value = true
    const validateContractForm = contractForm.value?.form.validate()
    const applicationId = applicationDetailsStore?.application?._id
    const licenseId = ref('')

    if (!validateContractForm) {
      sendToast(i18n.t('validationFailed'), undefined, 'error')
      finishLoading.value = false
      return
    } else {
      contractRequestBody.value = contractForm.value?.form.fields
    }

    if (
      !licenseModelRequestBody.value ||
      !contractRequestBody.value ||
      !applicationId
    )
      return

    // create license model
    await saveNewSoftwareLicense(licenseModelRequestBody.value, applicationId)
      .then((res) => {
        licenseId.value = (res as SoftwareLicense)._id
      })
      .catch(() => {
        return
      })

    // create contract
    await contractStore
      .createContract(licenseId.value, contractRequestBody.value)
      .catch(() => {
        return
      })

    sendToast(i18n.t('licenseSaved'), undefined, 'success')
    emit('add')
    finishLoading.value = false
    visible.value = false
  }
</script>

<style scoped lang="scss">
  .steps-wrapper ::v-deep(.step-buttons--back) {
    transition: all 0.1s ease-in-out;
    margin-left: 0.25rem;

    &:hover {
      scale: 1;
      transform: translateX(2px);
    }
  }

  .steps-wrapper ::v-deep(.step-buttons--next) {
    transition: all 0.1s ease-in-out;
    margin-right: 0.25rem;
    &:hover {
      scale: 1;
      transform: translateX(-2px);
    }
  }
</style>
